'use client';

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  RadioGroup,
  Radio,
  FormControlLabel,
  Typography,
  CircularProgress,
} from '@mui/material';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { useTranslations } from 'next-intl';

export type RefreshFrequency = 'daily' | 'weekly' | 'monthly' | 'none';

interface AutoRefreshPromptDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (frequency: RefreshFrequency) => void;
  isSaving?: boolean;
  sourceUrl?: string;
}

export function AutoRefreshPromptDialog({
  open,
  onClose,
  onSave,
  isSaving = false,
  sourceUrl,
}: AutoRefreshPromptDialogProps) {
  const t = useTranslations('creator.contentRefresh');
  const [frequency, setFrequency] = useState<RefreshFrequency>('none');

  // Reset frequency when dialog opens
  useEffect(() => {
    if (open) setFrequency('none');
  }, [open]);

  const handleClose = () => {
    setFrequency('none');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AutorenewIcon color="primary" />
        {t('frequencyQuestion')}
      </DialogTitle>
      <DialogContent>
        {sourceUrl && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2, wordBreak: 'break-all' }}
          >
            {sourceUrl}
          </Typography>
        )}
        <RadioGroup
          value={frequency}
          onChange={(e) => setFrequency(e.target.value as RefreshFrequency)}
        >
          <FormControlLabel
            value="daily"
            control={<Radio />}
            label={t('daily')}
          />
          <FormControlLabel
            value="weekly"
            control={<Radio />}
            label={t('weekly')}
          />
          <FormControlLabel
            value="monthly"
            control={<Radio />}
            label={t('monthly')}
          />
          <FormControlLabel
            value="none"
            control={<Radio />}
            label={t('skip')}
          />
        </RadioGroup>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSaving}>
          {t('skip')}
        </Button>
        <Button
          onClick={() => onSave(frequency)}
          variant="contained"
          disabled={isSaving}
          startIcon={
            isSaving ? <CircularProgress size={16} /> : undefined
          }
        >
          {t('save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
